import { Camera, Check, Play } from 'lucide-react';
import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VADA_FAZE_VZNIKU } from '@helfr/domain';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { DenikPamet, Ukol, Vada } from '../../api/types.js';
import type { TerenKontext } from '../../components/TerenLayout.js';
import {
  Badge,
  Chyba,
  Input,
  Nacitani,
  Select,
  Textarea,
  VelkeTlacitko,
} from '../../components/ui.js';
import {
  UKOL_DALSI_STAV,
  UKOL_STAV_BARVA,
  UKOL_STAV_LABEL,
  VADA_DALSI_STAV,
  VADA_FAZE_LABEL,
  VADA_STAV_BARVA,
  VADA_STAV_LABEL,
} from '../../lib/labels.js';
import { DatumVolba } from './DatumVolba.js';
import { denPlus, terminInfo } from './datum.js';

/**
 * Úkoly + vady v terénu: jednoduchý seznam na zakázce, změna stavu JEDNÍM
 * velkým tlačítkem (nový → probíhá → hotovo; otevřeno → opraveno →
 * překontrolováno), detail s fotkami. Vada se zakládá s fází vzniku
 * (nedolepitelný atribut) a fotkou. Žádný workflow engine.
 */
export function UkolyVadyPage() {
  const { zakazka } = useOutletContext<TerenKontext>();
  const [zalozka, setZalozka] = useState<'ukoly' | 'vady'>('ukoly');
  const [chyba, setChyba] = useState<string | null>(null);
  const [zakladam, setZakladam] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">Úkoly a vady</h1>
      {/* Přepínač je jediný „tab" v terénu — dvě velké poloviny. */}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Úkoly, nebo vady">
        <button
          type="button"
          aria-pressed={zalozka === 'ukoly'}
          className={`min-h-12 rounded-xl text-lg font-bold ${
            zalozka === 'ukoly' ? 'bg-primary text-white' : 'border-2 border-border-strong bg-surface'
          }`}
          onClick={() => setZalozka('ukoly')}
        >
          Úkoly
        </button>
        <button
          type="button"
          aria-pressed={zalozka === 'vady'}
          className={`min-h-12 rounded-xl text-lg font-bold ${
            zalozka === 'vady' ? 'bg-primary text-white' : 'border-2 border-border-strong bg-surface'
          }`}
          onClick={() => setZalozka('vady')}
        >
          Vady
        </button>
      </div>

      {chyba ? <Chyba>{chyba}</Chyba> : null}

      {!zakladam && (
        <VelkeTlacitko varianta="secondary" onClick={() => setZakladam(true)}>
          {zalozka === 'ukoly' ? '+ NOVÝ ÚKOL' : '+ NOVÁ VADA'}
        </VelkeTlacitko>
      )}
      {zakladam && zalozka === 'ukoly' && (
        <NovyUkol zakazkaId={zakazka.id} zavri={() => setZakladam(false)} naChybu={setChyba} />
      )}
      {zakladam && zalozka === 'vady' && (
        <NovaVada
          zakazkaId={zakazka.id}
          objekty={zakazka.objekty ?? []}
          zavri={() => setZakladam(false)}
          naChybu={setChyba}
        />
      )}

      {zalozka === 'ukoly' ? (
        <SeznamUkolu zakazkaId={zakazka.id} naChybu={setChyba} />
      ) : (
        <SeznamVad zakazkaId={zakazka.id} naChybu={setChyba} />
      )}
    </div>
  );
}

function SeznamUkolu({
  zakazkaId,
  naChybu,
}: {
  zakazkaId: string;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const { data: ukoly, isLoading } = useQuery({
    queryKey: ['ukoly', zakazkaId],
    queryFn: () => api.get<Ukol[]>(`/api/tasks?zakazkaId=${encodeURIComponent(zakazkaId)}`),
  });
  const [otevreny, setOtevreny] = useState<string | null>(null);

  const zmenStav = useMutation({
    mutationFn: ({ id, stav }: { id: string; stav: string }) =>
      api.patch<Ukol>(`/api/tasks/${id}`, { stav }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['ukoly', zakazkaId] }),
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  if (isLoading) return <Nacitani />;
  if (!ukoly?.length) {
    return (
      <p className="rounded-xl border border-border bg-surface p-4 text-base text-text-secondary">
        Žádné úkoly. Nový založíte tlačítkem + NOVÝ ÚKOL, vadu i s fotkou v záložce Vady.
      </p>
    );
  }

  // A4.3: otevřené nahoru, hotové/zamítnuté sbalit — seznam za měsíc nezhluchne.
  const hotoveStavy: Ukol['stav'][] = ['hotovo', 'zamitnuto'];
  const otevrene = ukoly.filter((u) => !hotoveStavy.includes(u.stav));
  const hotove = ukoly.filter((u) => hotoveStavy.includes(u.stav));

  const polozka = (u: Ukol) => {
    const dalsi = UKOL_DALSI_STAV[u.stav];
    return (
      <li key={u.id} className="rounded-xl border border-border bg-surface p-4">
        <button
          type="button"
          className="flex min-h-12 w-full items-center justify-between gap-2 text-left"
          onClick={() => setOtevreny(otevreny === u.id ? null : u.id)}
        >
          <span className="min-w-0 text-lg font-semibold">{u.nazev}</span>
          <Badge barva={UKOL_STAV_BARVA[u.stav]}>{UKOL_STAV_LABEL[u.stav]}</Badge>
        </button>
        {otevreny === u.id && (
          <DetailPolozky
            popis={u.popis}
            objekt={u.objekt}
            termin={u.termin}
            prirazeno={
              u.prirazenoUser?.jmeno ?? u.prirazenoSubdodavatel?.nazev ?? u.prirazenoProfese
            }
            detailUrl={`/api/tasks/${u.id}`}
          />
        )}
        {dalsi ? (
          <VelkeTlacitko
            className="mt-3"
            disabled={zmenStav.isPending}
            onClick={() => zmenStav.mutate({ id: u.id, stav: dalsi })}
          >
            {u.stav === 'novy' ? (
              <><Play size={18} strokeWidth={2} aria-hidden="true" /> ZAČÍT</>
            ) : (
              <><Check size={18} strokeWidth={2} aria-hidden="true" /> HOTOVO</>
            )}
          </VelkeTlacitko>
        ) : null}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {otevrene.length === 0 ? (
        <p className="text-base text-text-secondary">Žádný otevřený úkol — vše hotové.</p>
      ) : (
        <ul className="flex flex-col gap-3">{otevrene.map(polozka)}</ul>
      )}
      {hotove.length > 0 ? (
        <details className="rounded-xl border border-border bg-surface">
          <summary className="min-h-12 cursor-pointer px-4 py-3 text-base font-semibold">
            Hotové ({hotove.length}) — zobrazit
          </summary>
          <ul className="flex flex-col gap-3 p-4 pt-0">{hotove.map(polozka)}</ul>
        </details>
      ) : null}
    </div>
  );
}

function SeznamVad({
  zakazkaId,
  naChybu,
}: {
  zakazkaId: string;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const { data: vady, isLoading } = useQuery({
    queryKey: ['vady', zakazkaId],
    queryFn: () => api.get<Vada[]>(`/api/defects?zakazkaId=${encodeURIComponent(zakazkaId)}`),
  });
  const [otevrena, setOtevrena] = useState<string | null>(null);

  const zmenStav = useMutation({
    mutationFn: ({ id, stav }: { id: string; stav: string }) =>
      api.patch<Vada>(`/api/defects/${id}`, { stav }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['vady', zakazkaId] }),
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  if (isLoading) return <Nacitani />;
  if (!vady?.length) {
    return (
      <p className="rounded-xl border border-border bg-surface p-4 text-base text-text-secondary">
        Žádné vady. Novou i s fotkou založíte tlačítkem + NOVÁ VADA.
      </p>
    );
  }

  // A4.3: otevřené a opravené nahoru, překontrolované (uzavřené) sbalit.
  const otevrene = vady.filter((v) => v.stav !== 'prekontrolovano');
  const hotove = vady.filter((v) => v.stav === 'prekontrolovano');

  const polozka = (v: Vada) => {
    const dalsi = VADA_DALSI_STAV[v.stav];
    return (
      <li key={v.id} className="rounded-xl border border-border bg-surface p-4">
        <button
          type="button"
          className="flex min-h-12 w-full items-center justify-between gap-2 text-left"
          onClick={() => setOtevrena(otevrena === v.id ? null : v.id)}
        >
          <span className="min-w-0 text-lg font-semibold">{v.popis}</span>
          <Badge barva={VADA_STAV_BARVA[v.stav]}>{VADA_STAV_LABEL[v.stav]}</Badge>
        </button>
        <p className="text-sm text-text-secondary">
          Kdy se přišlo: {VADA_FAZE_LABEL[v.fazeVzniku]}
        </p>
        {otevrena === v.id && (
          <DetailPolozky
            objekt={v.objekt}
            termin={v.termin}
            prirazeno={v.kdoOpravi?.nazev}
            detailUrl={`/api/defects/${v.id}`}
          />
        )}
        {dalsi ? (
          <VelkeTlacitko
            className="mt-3"
            disabled={zmenStav.isPending}
            onClick={() => zmenStav.mutate({ id: v.id, stav: dalsi })}
          >
            {v.stav === 'otevreno' ? (
              <><Check size={18} strokeWidth={2} aria-hidden="true" /> OPRAVENO</>
            ) : (
              <><Check size={18} strokeWidth={2} aria-hidden="true" /> PŘEKONTROLOVÁNO</>
            )}
          </VelkeTlacitko>
        ) : null}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {otevrene.length === 0 ? (
        <p className="text-base text-text-secondary">Žádná otevřená vada — vše překontrolováno.</p>
      ) : (
        <ul className="flex flex-col gap-3">{otevrene.map(polozka)}</ul>
      )}
      {hotove.length > 0 ? (
        <details className="rounded-xl border border-border bg-surface">
          <summary className="min-h-12 cursor-pointer px-4 py-3 text-base font-semibold">
            Hotové ({hotove.length}) — zobrazit
          </summary>
          <ul className="flex flex-col gap-3 p-4 pt-0">{hotove.map(polozka)}</ul>
        </details>
      ) : null}
    </div>
  );
}

/** Termín s českým datem a lidskou příponou (dnes/zítra/po termínu). */
function TerminRadek({ termin }: { termin: string }) {
  const info = terminInfo(termin);
  return (
    <p>
      Termín: {info.text}
      {info.pripona ? (
        <span className={info.poTerminu ? 'font-semibold text-error-text' : 'text-text-secondary'}>
          {' '}
          — {info.pripona}
        </span>
      ) : null}
    </p>
  );
}

/** Detail rozbalené položky — fotky se dotahují až při rozbalení. */
function DetailPolozky({
  popis,
  objekt,
  termin,
  prirazeno,
  detailUrl,
}: {
  popis?: string | null;
  objekt?: { kod: string; nazev: string } | null;
  termin?: string | null;
  prirazeno?: string | null;
  detailUrl: string;
}) {
  const { data } = useQuery({
    queryKey: ['detail', detailUrl],
    queryFn: () =>
      api.get<{ prilohy: { id: string; popis?: string | null; nazev: string }[] }>(detailUrl),
  });
  return (
    <div className="mt-2 flex flex-col gap-1 text-base">
      {popis ? <p className="whitespace-pre-wrap">{popis}</p> : null}
      {objekt ? (
        <p>
          Objekt: {objekt.kod} — {objekt.nazev}
        </p>
      ) : null}
      {termin ? <TerminRadek termin={termin} /> : null}
      {prirazeno ? <p>Přiřazeno: {prirazeno}</p> : null}
      {data && data.prilohy.length > 0 && (
        <div className="mt-1 flex gap-2 overflow-x-auto">
          {data.prilohy.map((p) => (
            <img
              key={p.id}
              src={`/api/diary/prilohy/${p.id}/soubor`}
              alt={p.popis ?? p.nazev}
              className="h-24 w-24 shrink-0 rounded-lg border border-border object-cover"
              loading="lazy"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NovyUkol({
  zakazkaId,
  zavri,
  naChybu,
}: {
  zakazkaId: string;
  zavri: () => void;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [nazev, setNazev] = useState('');
  const [komu, setKomu] = useState<string | null>(null); // subdodavatelId
  const [maTermin, setMaTermin] = useState(false);
  const [termin, setTermin] = useState(denPlus(1));
  const [foto, setFoto] = useState<File | null>(null);
  const [odesilam, setOdesilam] = useState(false);
  // Nepovinná pole (Komu, Termín, Foto) schovaná za „+ víc (nepovinné)" —
  // základ je jen „Co se má provést" (blueprint 16 §15). Cesta zpět jedním ťuknutím.
  const [viceOtevreno, setViceOtevreno] = useState(false);

  // Komu (A4.1): subdodavatelé z paměti deníku (žádný extra endpoint v terénu).
  const { data: pamet } = useQuery({
    queryKey: ['denik-pamet', zakazkaId],
    queryFn: () =>
      api.get<DenikPamet>(`/api/diary/memory?zakazkaId=${encodeURIComponent(zakazkaId)}`),
  });
  const subky = (pamet?.firmy ?? []).filter(
    (f): f is { nazev: string; subdodavatelId: string } => !!f.subdodavatelId,
  );

  const zaloz = async () => {
    setOdesilam(true);
    naChybu(null);
    try {
      const ukol = await api.post<Ukol>('/api/tasks', {
        zakazkaId,
        nazev,
        ...(komu ? { prirazenoSubdodavatelId: komu } : {}),
        ...(maTermin && termin
          ? { termin: new Date(`${termin}T08:00:00.000Z`).toISOString() }
          : {}),
      });
      if (foto) {
        const form = new FormData();
        form.set('soubor', foto, foto.name);
        form.set('nazevSouboru', foto.name || 'ukol.jpg');
        form.set('mimeType', foto.type || 'image/jpeg');
        await api.upload(`/api/tasks/${ukol.id}/prilohy`, form);
      }
      void queryClient.invalidateQueries({ queryKey: ['ukoly', zakazkaId] });
      zavri();
    } catch (err) {
      naChybu(hlaskaZChyby(err));
    } finally {
      setOdesilam(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-2 text-lg font-bold">Nový úkol</h2>
      <div className="flex flex-col gap-3">
        <Input
          aria-label="Název úkolu"
          placeholder="Co se má provést…"
          value={nazev}
          onChange={(e) => setNazev(e.target.value)}
        />

        {/* Nepovinná pole za jedním „+ víc (nepovinné)" — základ je jen „Co se
            má provést" (blueprint 16 §15). */}
        {!viceOtevreno ? (
          <button
            type="button"
            className="min-h-12 self-start font-semibold text-primary underline underline-offset-2"
            onClick={() => setViceOtevreno(true)}
          >
            + víc (nepovinné)
          </button>
        ) : (
          <>
            {subky.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-base font-semibold">Komu (nepovinné)</span>
                <div className="flex flex-wrap gap-2">
                  {subky.map((f) => (
                    <button
                      key={f.subdodavatelId}
                      type="button"
                      aria-pressed={komu === f.subdodavatelId}
                      className={`min-h-12 rounded-full border-2 px-4 text-base font-semibold ${
                        komu === f.subdodavatelId
                          ? 'border-primary bg-primary-tint text-primary'
                          : 'border-border-strong bg-surface'
                      }`}
                      onClick={() => setKomu(komu === f.subdodavatelId ? null : f.subdodavatelId)}
                    >
                      {f.nazev}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {maTermin ? (
              <DatumVolba popisek="Termín" hodnota={termin} onZmena={setTermin} />
            ) : (
              <button
                type="button"
                className="min-h-12 self-start font-semibold text-primary underline underline-offset-2"
                onClick={() => setMaTermin(true)}
              >
                + Termín (nepovinné)
              </button>
            )}

            <label className="flex min-h-14 cursor-pointer items-center justify-center rounded-xl border-2 border-border-strong bg-surface px-4 text-lg font-bold hover:bg-bg">
              <Camera size={18} strokeWidth={2} aria-hidden="true" className="mr-2 inline" />
              {foto ? foto.name : 'FOTO (nepovinné)'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                aria-label="Foto úkolu"
                onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
              />
            </label>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <VelkeTlacitko disabled={!nazev.trim() || odesilam} onClick={() => void zaloz()}>
            {odesilam ? 'Zakládám…' : 'Založit'}
          </VelkeTlacitko>
          <VelkeTlacitko varianta="secondary" onClick={zavri}>
            Zrušit
          </VelkeTlacitko>
        </div>
      </div>
    </section>
  );
}

function NovaVada({
  zakazkaId,
  objekty,
  zavri,
  naChybu,
}: {
  zakazkaId: string;
  objekty: { id: string; kod: string; nazev: string }[];
  zavri: () => void;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [popis, setPopis] = useState('');
  const [fazeVzniku, setFazeVzniku] = useState<string>('prubezna');
  const [objektId, setObjektId] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [odesilam, setOdesilam] = useState(false);

  const zaloz = async () => {
    setOdesilam(true);
    naChybu(null);
    try {
      const vada = await api.post<Vada>('/api/defects', {
        zakazkaId,
        popis,
        fazeVzniku,
        objektId: objektId || null,
      });
      if (foto) {
        const form = new FormData();
        form.set('soubor', foto, foto.name);
        form.set('nazevSouboru', foto.name || 'vada.jpg');
        form.set('mimeType', foto.type || 'image/jpeg');
        await api.upload(`/api/defects/${vada.id}/prilohy`, form);
      }
      void queryClient.invalidateQueries({ queryKey: ['vady', zakazkaId] });
      zavri();
    } catch (err) {
      naChybu(hlaskaZChyby(err));
    } finally {
      setOdesilam(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-2 text-lg font-bold">Nová vada</h2>
      <div className="flex flex-col gap-3">
        <Textarea
          aria-label="Popis vady"
          rows={3}
          placeholder="Co je špatně…"
          value={popis}
          onChange={(e) => setPopis(e.target.value)}
        />
        <label className="block">
          <span className="mb-1 block text-base font-semibold">Kdy se na vadu přišlo?</span>
          <Select
            aria-label="Kdy se na vadu přišlo?"
            value={fazeVzniku}
            onChange={(e) => setFazeVzniku(e.target.value)}
          >
            {VADA_FAZE_VZNIKU.map((f) => (
              <option key={f} value={f}>
                {VADA_FAZE_LABEL[f]}
              </option>
            ))}
          </Select>
          <span className="mt-1 block text-sm text-text-secondary">
            při práci = průběžná · při přejímce · v záruce (reklamace)
          </span>
        </label>
        {objekty.length > 0 && (
          <label className="block">
            <span className="mb-1 block text-base font-semibold">Objekt</span>
            <Select
              aria-label="Objekt"
              value={objektId}
              onChange={(e) => setObjektId(e.target.value)}
            >
              <option value="">— bez objektu —</option>
              {objekty.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.kod} — {o.nazev}
                </option>
              ))}
            </Select>
          </label>
        )}
        <label className="flex min-h-14 cursor-pointer items-center justify-center rounded-xl border-2 border-border-strong bg-surface px-4 text-lg font-bold hover:bg-bg">
          <Camera size={18} strokeWidth={2} aria-hidden="true" className="mr-2 inline" />
          {foto ? foto.name : 'VYFOTIT'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label="Foto vady"
            onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <VelkeTlacitko disabled={!popis.trim() || odesilam} onClick={() => void zaloz()}>
            {odesilam ? 'Zakládám…' : 'Založit vadu'}
          </VelkeTlacitko>
          <VelkeTlacitko varianta="secondary" onClick={zavri}>
            Zrušit
          </VelkeTlacitko>
        </div>
      </div>
    </section>
  );
}
